import DemoNotice from './DemoNotice'
import BackRow from './BackButton'
import { TranslatedBody } from './Translated'
import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../components/Icon'
import { imageUrl, worx, type Message, type Thread } from './client'
import { initials } from './Jobs'
import Report from './Report'
import { tw } from './strings'
import { done } from './feel'

/**
 * The mailbox.
 *
 * Deliberately an inbox and not a chat, for the reason the schema gives: an
 * employer reads applications on Sunday night and a worker checks between
 * shifts, and a mailbox makes a day's delay ordinary where a chat window makes
 * it a snub.
 *
 * ── What that buys, concretely ──────────────────────────────────────────────
 *
 *  - A subject line. Both sides see "Line Cook — María Rodríguez", so a thread
 *    is identifiable in a list of ten without opening it. Nobody types it.
 *  - Unread as weight, not a badge count. A bold row is the one thing every
 *    person already knows how to read.
 *  - A sender column. On the employer side two managers write into one thread,
 *    and a chat bubble aligned right cannot say which of them it was.
 *  - Latest at the bottom, oldest at the top, newest thread first in the list —
 *    the arrangement every mail client has agreed on, so nobody has to learn it.
 */
export default function Mailbox({
  openThreadId,
  side,
}: {
  openThreadId?: string | null
  /*
   * Which side's mailbox this is.
   *
   * One account can hold both roles, and without this the employer's inbox
   * shows the jobs that person applied to themselves, filed under "Applicant
   * conversations". The threads are the same rows either way; what changes is
   * which of them belong on the screen you are standing on.
   */
  side: 'worker' | 'employer'
}) {
  const [threads, setThreads] = useState<Thread[] | null>(null)
  const [open, setOpen] = useState<string | null>(openThreadId ?? null)
  const [q, setQ] = useState('')
  const [onlyUnread, setOnlyUnread] = useState(false)

  const load = useCallback(async () => {
    try {
      setThreads((await worx.threads()).threads)
    } catch {
      setThreads([])
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => { if (openThreadId) setOpen(openThreadId) }, [openThreadId])

  if (open) {
    return (
      <Conversation
        id={open}
        onBack={() => {
          setOpen(null)
          // Reload on the way out: the thread was just marked read, and a list
          // that still shows it bold is the badge lying to the person who just
          // dealt with it.
          void load()
        }}
      />
    )
  }

  if (!threads) return <p className="muted">…</p>
  if (!threads.some((t) => t.side === side)) return <p className="muted w-empty">{tw('w.mail.none')}</p>

  const mine = threads.filter((t) => t.side === side)
  const unreadTotal = mine.filter((t) => t.unread).length
  const needle = q.trim().toLowerCase()
  const shown = mine
    .filter((t) => !onlyUnread || t.unread)
    .filter((t) => {
      if (!needle) return true
      const who = t.side === 'worker' ? t.employerName : t.workerName
      return [who, t.jobTitle, t.lastSnippet ?? ''].some((v) => v.toLowerCase().includes(needle))
    })

  /*
   * The heading names who is on the other end, not what the screen is.
   *
   * An employer's mailbox is full of applicants; a worker's is full of
   * businesses. The same list, read from two sides, is two different things to
   * the person reading it — and the word is free.
   */
  const heading = side === 'employer'
    ? tw('w.mail.applicantConvos')
    : tw('w.mail.employerConvos')

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{tw('w.mail.screenTitle')}</h1>

      <div className="w-search">
        <Icon name="search" size={20} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tw('w.mail.search')}
          aria-label={tw('w.mail.search')}
        />
      </div>

      <div className="w-fchips" role="group" aria-label={tw('w.mail.screenTitle')}>
        <button
          className={onlyUnread ? 'w-fchip' : 'w-fchip on'}
          aria-pressed={!onlyUnread}
          onClick={() => setOnlyUnread(false)}
        >
          {tw('w.mail.all')}
        </button>
        <button
          className={onlyUnread ? 'w-fchip on' : 'w-fchip'}
          aria-pressed={onlyUnread}
          onClick={() => setOnlyUnread(true)}
        >
          {tw('w.mail.unread')}
          {/* Coral, and only when there are any: an unread count of zero is not
              a badge, it is a fact best said by the absence of one. */}
          {unreadTotal > 0 && <span className="w-fchip-n w-fchip-n-due">{unreadTotal}</span>}
        </button>
      </div>

      <h2 className="w-group-head">{heading}</h2>

      {shown.length === 0 && (
        <p className="muted w-empty">{onlyUnread ? tw('w.mail.allRead') : tw('w.mail.noMatch')}</p>
      )}

      <div className="w-convos">
        {shown.map((t) => {
          const who = t.side === 'worker' ? t.employerName : t.workerName
          const face = t.side === 'worker' ? t.employerLogoId : t.workerPhotoId
          const photo = imageUrl(face)
          return (
            <button key={t.id} className="w-convo" onClick={() => setOpen(t.id)}>
              {photo
                ? <img className="w-avatar" src={photo} alt="" width={56} height={56} loading="lazy" decoding="async" />
                : <span className="w-avatar" aria-hidden="true">{initials(who)}</span>}

              <span className="w-convo-main">
                <span className="w-convo-top">
                  <b className="w-convo-who">{who}</b>
                  <span className={`w-stat w-stat-${t.applicationStatus}`}>
                    <span className="w-stat-dot" aria-hidden="true" />
                    {tw(`w.status.${t.applicationStatus}` as never)}
                  </span>
                </span>
                {/* The job, not the subject line: the subject repeats the name
                    that is already two lines above it. */}
                <span className="w-convo-job">{t.jobTitle}{t.isDemo && <> · {tw('w.demo.badge')}</>}</span>
                <span className="w-convo-snippet">{t.lastSnippet ?? tw('w.mail.empty')}</span>
              </span>

              <span className="w-convo-side">
                <span className="w-convo-when">{when(t.lastMessageAt)}</span>
                {t.unreadCount > 0 && <span className="w-convo-n">{t.unreadCount}</span>}
              </span>
            </button>
          )
        })}
      </div>

      <p className="muted small w-list-hint">{tw('w.mail.hint')}</p>
    </div>
  )
}

function Conversation({ id, onBack }: { id: string; onBack: () => void }) {
  const [thread, setThread] = useState<Thread | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [blockedByMe, setBlockedByMe] = useState(false)
  const [blockedByThem, setBlockedByThem] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [relayed, setRelayed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const end = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    const r = await worx.thread(id)
    setThread(r.thread)
    setMessages(r.messages)
    setBlockedByMe(r.blockedByMe)
    setBlockedByThem(r.blockedByThem)
  }, [id])

  useEffect(() => { void load().catch(() => setError(tw('w.error.conversationLoad'))) }, [load])
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }) }, [messages.length])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body) return
    setBusy(true)
    setError(null)
    try {
      const r = await worx.sendMessage(id, body)
      done()
      setDraft('')
      setRelayed(r.relayed)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.send'))
    } finally {
      setBusy(false)
    }
  }

  if (error && !thread) return <p className="error">{error}</p>
  if (!thread) return <p className="muted">…</p>

  const ended = blockedByMe || blockedByThem

  return (
    <div className="w-thread">
      <header className="w-thread-head">
        <BackRow onBack={onBack}>
          <h2>{thread.subject}</h2>
        </BackRow>
        <p className="muted small">
          {thread.side === 'worker' ? thread.employerName : thread.workerName} · {thread.jobTitle}
        </p>
      </header>
      {thread.isDemo && <DemoNotice practice />}

      <ol className="w-messages">
        {messages.map((m) => (
          <li key={m.id} className={m.side === thread.side ? 'w-msg mine' : 'w-msg'}>
            <div className="w-msg-head">
              <strong>{m.senderName}</strong>
              <span className="muted small">{when(m.createdAt)}</span>
            </div>
            <TranslatedBody className="w-msg-body" text={m.body} translated={m.translated} from={m.from} />
            {m.relayedAs && <span className="muted small">{tw('w.mail.relayed')}</span>}
          </li>
        ))}
        {!messages.length && <li className="muted w-empty">{tw('w.mail.empty')}</li>}
        <div ref={end} />
      </ol>

      {ended ? (
        <div className="w-thread-ended">
          <p className="muted">{tw('w.mail.blocked')}</p>
          {/* Only the person who ended it can reopen it. Offering the button to
              the other side would be a "reopen" that fails. */}
          {blockedByMe && (
            <button onClick={() => void worx.blockThread(id, false).then(load)}>{tw('w.mail.unblock')}</button>
          )}
        </div>
      ) : (
        <form className="w-compose" onSubmit={send}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={tw('w.mail.placeholder')}
            rows={3}
            maxLength={4000}
          />
          {error && <p className="error">{error}</p>}
          {relayed && <p className="muted small">{tw('w.mail.relayed')}</p>}
          <div className="w-compose-actions">
            <button className="primary" type="submit" disabled={busy || !draft.trim()}>
              {busy ? '…' : tw('w.mail.send')}
            </button>
            <button type="button" className="link" onClick={() => void worx.blockThread(id, true).then(load)}>
              {tw('w.mail.block')}
            </button>
            {/* Blocking ends it for me; reporting tells somebody why. They are
                different acts and both have to be here. */}
            <Report subjectType="message" subjectId={id} label={tw('w.report.convo')} />
          </div>
        </form>
      )}
    </div>
  )
}

/**
 * A timestamp the way a mail client shows one: a time today, a weekday this
 * week, a date beyond that. The absolute value is always available on hover —
 * "3 days ago" is friendlier and useless for "when exactly did they reply".
 */
function when(ms: number | null): string {
  if (!ms) return ''
  const d = new Date(ms)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
  // "Yesterday" by CALENDAR day, not by 24 hours: a message at 11pm last night
  // is yesterday's at 9am, and "23 hours ago" is not how anybody says it.
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return tw('w.mail.yesterday')
  if (now.getTime() - ms < 6 * 24 * 60 * 60 * 1000) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}
